import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import FeatureNotEnabled from '../components/FeatureNotEnabled';
import { featureFlagsService } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { 
  Wallet, 
  AlertCircle,
  AlertTriangle,
  CreditCard,
  Building,
  FileText,
  DollarSign,
  Clock
} from 'lucide-react';

/**
 * Payroll / Settlements Integration Module (Phase 6 - Scaffolded)
 * 
 * FEATURE FLAG: payroll_integration_enabled
 * ACCESS: Super Admin only
 * STATUS: Placeholder - No banking connections, no exports
 */
const PayrollIntegrationPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Super Admin only
  const allowedRoles = ['super_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        const enabledFeatures = await featureFlagsService.getEnabledFeatures();
        setFeatureEnabled(enabledFeatures.includes('payroll_integration_enabled'));
      } catch (err) {
        console.error('Failed to check feature flag:', err);
        setFeatureEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      checkFeature();
    }
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return <FeatureNotEnabled featureName="Payroll / Settlements Integration" />;
  }

  // Placeholder content when feature IS enabled (future-ready scaffold)
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Payroll / Settlements' }]} />

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-8 h-8 text-purple-600" />
            Payroll / Settlements Integration
          </h1>
          <p className="text-gray-600 mt-1">Settlement processing and payout provider integrations</p>
        </div>

        {/* Critical Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">No Payments Are Processed in This Module</div>
            <div className="text-sm text-red-700">
              This is a preview interface only. No banking connections, ACH transfers, or payment processing is active.
              All financial operations require proper integration setup.
            </div>
          </div>
        </div>

        {/* Preview Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-800">Preview Mode</div>
            <div className="text-sm text-blue-700">
              This module displays a read-only preview layout. No actual data or integrations are connected.
            </div>
          </div>
        </div>

        {/* Placeholder Content */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Settlements Overview */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-500">
                <FileText className="w-5 h-5" />
                Settlements Overview (Preview)
              </CardTitle>
              <CardDescription>Coming Soon</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-400">Pending Settlements</span>
                  <span className="text-gray-400 font-mono">--</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-400">Processing</span>
                  <span className="text-gray-400 font-mono">--</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-400">Completed This Month</span>
                  <span className="text-gray-400 font-mono">--</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400">Total Disbursed</span>
                  <span className="text-gray-400 font-mono">$--,---</span>
                </div>
              </div>
              <div className="mt-4 flex items-center text-gray-400 text-sm">
                <Clock className="w-4 h-4 mr-1" />
                Feature under development
              </div>
            </CardContent>
          </Card>

          {/* Payout Providers */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-500">
                <Building className="w-5 h-5" />
                Payout Providers (Not Connected)
              </CardTitle>
              <CardDescription>Coming Soon</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-gray-300" />
                    <span className="text-gray-400">ACH Direct Deposit</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">Not Connected</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-300" />
                    <span className="text-gray-400">Quick Pay</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">Not Connected</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-300" />
                    <span className="text-gray-400">Fuel Card Integration</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">Not Connected</span>
                </div>
              </div>
              <div className="mt-4 flex items-center text-gray-400 text-sm">
                <Clock className="w-4 h-4 mr-1" />
                Providers require configuration
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PayrollIntegrationPage;
